import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { NumericInput } from "@/ds/custom/NumericInput";
import { SectionRow } from "@/ds/custom/SectionRow";
import { Selector } from "@/ds/custom/Selector";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import type { PriceAlertDetailsModel } from "@/features/priceAlerts/api/models/PriceAlertDetailsModel";
import { usePriceAlertCreate } from "@/features/priceAlerts/api/usePriceAlertCreate";
import { usePriceAlertUpdate } from "@/features/priceAlerts/api/usePriceAlertUpdate";
import { PriceAlertDescription } from "@/features/priceAlerts/components/PriceAlertDescription";
import { PriceAlertTargetDisplay } from "@/features/priceAlerts/components/PriceAlertTargetDisplay";
import { priceAlertMetricHasStep, priceAlertMetricIsPercentage, priceAlertMetricStepIsRequired, PriceAlertMetricOptions, type PriceAlertMetric } from "@/features/priceAlerts/PriceAlertMetric";
import { PriceAlertStepOptions, PriceAlertStepSelectionOptions, type PriceAlertStep } from "@/features/priceAlerts/PriceAlertStep";
import type { PriceAlertTarget } from "@/features/priceAlerts/PriceAlertTarget";
import type { SelectedPriceAlertTarget } from "@/features/priceAlerts/SelectedPriceAlertTarget";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import type { Currency } from "@/lib/domain/Currency";
import type { PriceSource } from "@/lib/domain/PriceSource";
import { useCallback, useEffect, useMemo, useState, type FC, type PropsWithChildren } from "react";

export interface PriceAlertEditProps {
	priceAlert: PriceAlertDetailsModel | null;
	target: SelectedPriceAlertTarget | null;
	currency: Currency;
	priceSource: PriceSource;
	onChangeTarget: () => void;
	onClose: () => void;
}

export const PriceAlertEdit: FC<PriceAlertEditProps> = props => {
	const {
		priceAlert,
		target,
		currency,
		priceSource,
		onChangeTarget,
		onClose
	} = props;

	const {
		targetPhrase,
		currentValue,
		hasNoMarketPrice
	} = useMemo(() => getPriceAlertEditTargetView(target, priceAlert), [target, priceAlert]);

	const [metricType, setMetricType] = useState<PriceAlertMetric>(priceAlert?.metricType ?? "FixedValue");
	const [metric, setMetric] = useState(priceAlert?.metric ?? (currentValue ?? 0));
	const [step, setStep] = useState<PriceAlertStep | null>(priceAlert?.step ?? null);

	const createPriceAlert = usePriceAlertCreate();
	const updatePriceAlert = usePriceAlertUpdate();

	useNotifyMutationSuccess(createPriceAlert, "Price Alert Created", "Your price alert was created successfully.");
	useNotifyMutationSuccess(updatePriceAlert, "Price Alert Updated", "Your price alert was updated successfully.");

	const action = !priceAlert ? "create" : "update";
	const mutation = action === "create" ? createPriceAlert : updatePriceAlert;

	useEffect(() => {
		if (!mutation.isSuccess) {
			return;
		}

		onClose();
	}, [mutation.isSuccess]);

	const onMetricTypeChange = useCallback((newMetricType: PriceAlertMetric) => {
		setMetricType(newMetricType);
		setStep(current => {
			if (!priceAlertMetricHasStep(newMetricType)) {
				return null;
			}

			return priceAlertMetricStepIsRequired(newMetricType) ? current ?? "Step24Hours" : current;
		});

		if (action === "create") {
			setMetric(newMetricType === "FixedValue" ? (currentValue ?? 0) : 0);
		}
	}, [action, currentValue]);

	const onClick = useCallback(() => {
		if (isNaN(metric)) {
			return;
		}

		if (!priceAlert) {
			if (!target) {
				return;
			}

			createPriceAlert.mutate({
				targetType: target.targetType,
				targetId: getPriceAlertEditTargetId(target),
				metricType,
				metric,
				currency,
				step,
				priceSource
			});

			return;
		}
		const resolvedTarget = target
			? {
				targetType: target.targetType,
				targetId: getPriceAlertEditTargetId(target)
			}
			: {
				targetType: priceAlert.targetType,
				targetId: priceAlert.targetId
			};

		updatePriceAlert.mutate({
			priceAlertId: priceAlert.priceAlertId,
			requestModel: {
				enabled: priceAlert.enabled,
				metricType,
				metric,
				currency: priceAlert.currency,
				step,
				priceSource: priceAlert.priceSource,
				...resolvedTarget
			}
		});
	}, [createPriceAlert, updatePriceAlert, target, priceAlert, metricType, metric, currency, step, priceSource]);

	const isInvalid = useMemo(() => isPriceAlertMetricInvalid(metricType, metric, currentValue) || hasNoMarketPrice, [metricType, metric, currentValue, hasNoMarketPrice]);
	const isPercentageMetric = useMemo(() => priceAlertMetricIsPercentage(metricType), [metricType]);
	const metricLabel = useMemo(() => getPriceAlertMetricLabel(metricType), [metricType]);
	const descriptionCurrency = useMemo(() => !priceAlert ? currency : priceAlert.currency, [currency, priceAlert]);
	const descriptionPriceSource = useMemo(() => !priceAlert ? priceSource : priceAlert.priceSource, [priceSource, priceAlert]);

	return (
		<div>
			<div className="flex flex-col border-b p-3 gap-3 @md:flex-row @md:items-center">
				<div className="min-w-0 flex-1">
					<PriceAlertEditTargetHeader target={target} priceAlert={priceAlert} />
				</div>

				<Button variant="outline" className="w-full @md:w-56" onClick={onChangeTarget} disabled={mutation.isPending}>
					Change
				</Button>
			</div>

			{
				currentValue !== null &&
				<PriceAlertEditRow label="Current Value">
					<MoneyDisplay money={{ value: currentValue, currency: descriptionCurrency }} mode="Standard" preferredCurrency={descriptionCurrency} />
				</PriceAlertEditRow>
			}

			<PriceAlertEditRow label="Alert Type">
				<Selector
					className="w-full @md:w-56"
					placeholder="Alert Type"
					options={PriceAlertMetricOptions}
					value={metricType}
					onChange={onMetricTypeChange} />
			</PriceAlertEditRow>

			<PriceAlertEditRow label={metricLabel}>
				<NumericInput
					className="w-full @md:w-56"
					value={metric}
					onChange={setMetric}
					minValue={0}
					maxValue={isPercentageMetric ? 1000 : 500000}
					maxDecimalPlaces={isPercentageMetric ? 1 : 2}
					prefix={isPercentageMetric ? "%" : undefined} />
			</PriceAlertEditRow>

			{
				priceAlertMetricHasStep(metricType) &&
				<PriceAlertEditRow label="Time Window">
					<Selector
						className="w-full @md:w-56"
						placeholder="Time Window"
						options={priceAlertMetricStepIsRequired(metricType) ? PriceAlertStepOptions : PriceAlertStepSelectionOptions}
						value={step ?? "None"}
						onChange={value => setStep(value === "None" ? null : value)} />
				</PriceAlertEditRow>
			}

			<div className="type-description p-3 border-b">
				{
					hasNoMarketPrice ? (
						<div>
							This product does not currently have a value. A price alert cannot be created at this time.
						</div>
					) : (
						<PriceAlertDescription
							targetPhrase={targetPhrase}
							currentValue={currentValue}
							metricType={metricType}
							metric={metric}
							currency={descriptionCurrency}
							step={step}
							priceSource={descriptionPriceSource} />
					)
				}
			</div>

			{
				mutation.error &&
				<div className="p-3 border-b">
					<ErrorDisplay subtle={true} error={mutation.error} />
				</div>
			}

			<div className="p-3 flex @md:justify-end">
				<Button variant="outline" className="w-full @md:w-56 cursor-pointer" disabled={isInvalid || mutation.isPending} onClick={onClick}>
					{
						mutation.isPending ? <Spinner /> : "Confirm"
					}
				</Button>
			</div>
		</div>
	);
};

interface PriceAlertEditRowProps extends PropsWithChildren {
	label: string;
}

const PriceAlertEditRow = (props: PriceAlertEditRowProps) => {
	const {
		label,
		children
	} = props;

	return (
		<SectionRow title={label} className="text-sm">
			{children}
		</SectionRow>
	);
};

interface PriceAlertEditTargetHeaderProps {
	target: SelectedPriceAlertTarget | null;
	priceAlert: PriceAlertDetailsModel | null;
}

const PriceAlertEditTargetHeader = (props: PriceAlertEditTargetHeaderProps) => {
	const {
		target,
		priceAlert
	} = props;

	if (!target) {
		if (!priceAlert) {
			return;
		}

		return <PriceAlertTargetDisplay {...priceAlert} />;
	}

	if (target.targetType === "Variant") {
		return (
			<PriceAlertTargetDisplay
				targetType="Variant"
				targetName={target.product.name}
				targetDescription={target.variant.fcId}
				targetImageUrl={target.product.imageUrl} />
		);
	}

	return (
		<PriceAlertTargetDisplay
			targetType={target.targetType}
			targetName={target.collection.name}
			targetDescription={target.collection.description}
			targetImage={target.collection.image} />
	);
};

const getPriceAlertMetricLabel = (metricType: PriceAlertMetric): string => {
	switch (metricType) {
	case "FixedValue":
		return "Target Value";
	case "FlatGain":
		return "Gain Amount";
	case "FlatLoss":
		return "Loss Amount";
	case "PercentageGain":
		return "Gain Percentage";
	case "PercentageLoss":
		return "Loss Percentage";
	}
};

const getPriceAlertEditTargetId = (target: SelectedPriceAlertTarget): string => {
	return target.targetType === "Variant" ? target.variant.variantId : target.collection.collectionId;
};

const getPriceAlertTargetPhrase = (targetType: PriceAlertTarget, name: string): string => {
	switch (targetType) {
	case "Variant":
		return name;
	case "Collection":
		return `the total value of ${name}`;
	case "CollectionVariants":
		return `any product in ${name}`;
	}
};

const isPriceAlertMetricInvalid = (metricType: PriceAlertMetric, metric: number, currentValue: number | null): boolean => {
	if (metricType === "FixedValue" && currentValue !== null) {
		return metric === currentValue;
	}

	return metric <= 0;
};

interface PriceAlertEditTargetView {
	readonly targetPhrase: string;
	readonly currentValue: number | null;
	readonly hasNoMarketPrice: boolean;
}

const getPriceAlertEditTargetView = (target: SelectedPriceAlertTarget | null, priceAlert: PriceAlertDetailsModel | null): PriceAlertEditTargetView => {
	if (!target) {
		if (!priceAlert) {
			return {
				targetPhrase: "",
				currentValue: null,
				hasNoMarketPrice: true
			};
		}

		return {
			targetPhrase: getPriceAlertTargetPhrase(priceAlert.targetType, priceAlert.targetName),
			currentValue: null,
			hasNoMarketPrice: false
		};
	}

	switch (target.targetType) {
	case "Variant":
		return {
			targetPhrase: `${target.product.name} ${target.variant.variantName}`,
			currentValue: target.variant.marketPrice?.value ?? null,
			hasNoMarketPrice: !target.variant.marketPrice
		};
	case "Collection":
		return {
			targetPhrase: getPriceAlertTargetPhrase("Collection", target.collection.name),
			currentValue: target.collection.marketValue.value,
			hasNoMarketPrice: false
		};
	case "CollectionVariants":
		return {
			targetPhrase: getPriceAlertTargetPhrase("CollectionVariants", target.collection.name),
			currentValue: null,
			hasNoMarketPrice: false
		};
	}
};
